import { type JSX, useState } from "react";

import { TemplateEditor } from "features/templates/components/index";
import VoiceTemplate from "features/templates/components/VoiceTemplate";
import { TEMPLATE_TYPES } from "features/templates/constants";
import type {
  TemplateSchemaType,
  VoiceTemplateSchemaType,
} from "features/templates/schemas";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogFooter,
  OverlayDialogContent,
  OverlayDialogTitle,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";

import type { UseFormReturn } from "react-hook-form";

interface MobileTemplateProps {
  saveButton: JSX.Element;
  onClose: () => void;
  previewComponent: JSX.Element;
  updatePreview: () => void;
  form: UseFormReturn<TemplateSchemaType>;
}

// this is used for both editing and creating new templates on mobile, since the experience is identical
export default function MobileTemplate({
  saveButton,
  onClose,
  previewComponent,
  updatePreview,
  form,
}: MobileTemplateProps) {
  const [isShowingPreview, setIsShowingPreview] = useState(false);

  const { type, ...templateRest } = form.getValues();

  const isVoiceTemplate = form.getValues("type") === "VOICE_TEMPLATE";

  function showPreview() {
    updatePreview();
    setIsShowingPreview(true);
  }

  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <OverlayDialogContent>
          <OverlayDialogTitle>
            {"id" in templateRest ? "Edit" : "Create"}{" "}
            {TEMPLATE_TYPES[type].label.toLowerCase()}
          </OverlayDialogTitle>
          <DialogBody className="flex-1">
            <FormField
              label="Template name"
              htmlFor="templateName"
              className="mb-4"
              size="lg"
              error={form.formState.errors.name?.message}
            >
              <Input
                {...form.register("name")}
                hasError={!!form.formState.errors.name?.message}
                variant="lg"
              />
            </FormField>
            {isVoiceTemplate ? (
              <VoiceTemplate
                form={form as UseFormReturn<VoiceTemplateSchemaType>}
                isEditable
              />
            ) : (
              <TemplateEditor
                form={
                  form as UseFormReturn<
                    Exclude<TemplateSchemaType, VoiceTemplateSchemaType>
                  >
                }
                isEditable
              />
            )}
          </DialogBody>
          <DialogFooter className="justify-between">
            <DialogClose asChild>
              <Button variant="secondary-black">
                {"id" in templateRest ? "Cancel" : "Discard"}
              </Button>
            </DialogClose>
            {isVoiceTemplate ? (
              saveButton
            ) : (
              <Button variant="default-black" onClick={showPreview}>
                Preview
              </Button>
            )}
          </DialogFooter>
        </OverlayDialogContent>
      </Dialog>
      <Drawer open={isShowingPreview} onOpenChange={setIsShowingPreview}>
        <DrawerContent>
          <DrawerBody>{previewComponent}</DrawerBody>
          <DrawerFooter className="justify-end">
            <Button
              variant="secondary-black"
              onClick={() => setIsShowingPreview(false)}
            >
              Back
            </Button>
            {saveButton}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
